import { useState, useEffect } from 'react';
import { BuyNewHomeInputs, BuyNewHomeCalculationResults, BuyNewHomeMonthlyData, HomeInputs, TaxInputs, FilingStatus } from '../types';
import { STANDARD_DEDUCTIONS, SALT_CAPS } from '../constants';

export const calculateDeductibleInterest = (interest: number, mortgageBalance: number, filingStatus: FilingStatus) => {
    if (mortgageBalance <= 0) return 0;
    const limit = filingStatus === FilingStatus.MARRIED_FILING_SEPARATELY ? 375000 : 750000;
    const deductiblePortion = mortgageBalance > limit ? limit / mortgageBalance : 1;
    return interest * deductiblePortion;
};

// Helper to calc payment
const calculateMonthlyPayment = (principal: number, annualRate: number, months: number) => {
    if (annualRate === 0) return principal / months;
    const monthlyRate = annualRate / 100 / 12;
    return principal * (monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
};

/**
 * Calculates projected tax itemization information based on mortgage inputs.
 */
export const calculateItemizationInfo = (home: HomeInputs, tax: TaxInputs) => {
    const { purchasePrice, downPayment, interestRate, loanTerm, startDate, isARM, armFixedTerm, armSubsequentRate } = home;
    const { otherItemizedDeductions } = tax;
    const standardDeduction = STANDARD_DEDUCTIONS[tax.filingStatus];
    const loanAmount = purchasePrice * (1 - downPayment / 100);

    if (!loanAmount || !interestRate || !loanTerm) {
      return { standardDeduction, itemizedYears: [] };
    }

    const totalMonths = loanTerm * 12;
    let tempBalance = loanAmount;
    let pAndI = calculateMonthlyPayment(loanAmount, interestRate, totalMonths);

    const start = new Date(startDate);
    const startYear = start.getFullYear();
    const startMonth = start.getMonth();
    const baseSaltCap = SALT_CAPS[tax.filingStatus];

    const annualDeductibleInterest: { [year: number]: number } = {};
    
    for (let month = 1; month <= totalMonths; month++) {
      if (tempBalance <= 0) break;
      
      // Determine Rate
      let currentRate = interestRate;
      if (isARM && month > armFixedTerm * 12) {
          currentRate = armSubsequentRate;
          // Recast payment at adjustment trigger
          if (month === (armFixedTerm * 12) + 1) {
              pAndI = calculateMonthlyPayment(tempBalance, currentRate, totalMonths - (month - 1));
          }
      }
      
      const monthlyRate = currentRate / 100 / 12;
      const interest = tempBalance * monthlyRate;
      
      const year = startYear + Math.floor((startMonth + month - 1) / 12);
      if (!annualDeductibleInterest[year]) {
        annualDeductibleInterest[year] = 0;
      }
      
      const deductibleInterest = calculateDeductibleInterest(interest, tempBalance, tax.filingStatus);
      annualDeductibleInterest[year] += deductibleInterest;
      
      const principal = pAndI - interest;
      tempBalance -= principal;
    }
    
    const itemizedYears: number[] = [];
    for (const yearStr in annualDeductibleInterest) {
      const year = Number(yearStr);
      const yearsSinceStart = year - startYear;
      const currentYearSaltCap = baseSaltCap * Math.pow(1.01, yearsSinceStart);
      const cappedOtherDeductions = Math.min(otherItemizedDeductions, currentYearSaltCap);

      if (annualDeductibleInterest[year] + cappedOtherDeductions > standardDeduction) {
        itemizedYears.push(year);
      }
    }

    return { standardDeduction, itemizedYears };
};

export const calculateBuyNewHome = (inputs: BuyNewHomeInputs): BuyNewHomeCalculationResults | null => {
    const { home, tax, investment } = inputs;
    const { purchasePrice, downPayment, interestRate, loanTerm, startDate, closingCosts, insuranceSavings, appreciation, isARM, armFixedTerm, armSubsequentRate } = home;
    
    if (!purchasePrice || !interestRate || !loanTerm) {
        return null;
    }

    const { otherItemizedDeductions } = tax;
    const standardDeduction = STANDARD_DEDUCTIONS[tax.filingStatus];
    const baseSaltCap = SALT_CAPS[tax.filingStatus];
    
    // --- Initial Setup ---
    const loanAmount = purchasePrice * (1 - downPayment / 100);
    const downPaymentAmount = purchasePrice * (downPayment / 100);
    const totalMonths = loanTerm * 12;
    const start = new Date(startDate);
    const startYear = start.getFullYear();

    const monthlyAppreciationRate = appreciation / 100 / 12;
    
    // Initial P&I
    let pAndI = calculateMonthlyPayment(loanAmount, interestRate, totalMonths);
    if (isNaN(pAndI)) return null;
    
    // --- Simulation Initialization ---
    const monthlyData: BuyNewHomeMonthlyData[] = [];
    let homeValue = purchasePrice;
    
    // Scenario 1: Finance & Invest
    let mortgageBalance = loanAmount;
    let totalInterestPaid = 0;
    let totalPrincipalPaid = 0;
    let investedBalanceFinance = Math.max(0, purchasePrice - downPaymentAmount - closingCosts);
    let deductibleInterestThisYear = 0;
    let yearlyTaxSavings: {[year: number]: number} = {};

    // Scenario 2: Pay Cash & Invest
    let investedBalancePayCash = 0;

    const sortedLumpSums = [...(home.lumpSums || [])].sort((a, b) => a.yearsFromNow - b.yearsFromNow);
    let lumpSumIndex = 0;

    // Apply Year 0 Lump Sums
    while (lumpSumIndex < sortedLumpSums.length && sortedLumpSums[lumpSumIndex].yearsFromNow <= 0) {
        // Finance: User applies lump sum to mortage balance (reducing debt)
        mortgageBalance -= sortedLumpSums[lumpSumIndex].amount;
        // Pay Cash: User applies lump sum to investment
        investedBalancePayCash += sortedLumpSums[lumpSumIndex].amount;
        lumpSumIndex++;
    }

    for (let month = 1; month <= totalMonths; month++) {
      const currentDate = new Date(start);
      currentDate.setMonth(start.getMonth() + month - 1);
      const currentYear = currentDate.getFullYear();
      
      const yearsIntoSim = (month - 1) / 12;
      const monthlyReturnRate = yearsIntoSim < investment.initialReturnPeriod ? investment.initialReturnRate / 100 / 12 : investment.longTermReturnRate / 100 / 12;
      
      homeValue *= (1 + monthlyAppreciationRate);

      // --- Determine Rate for this month ---
      let currentRate = interestRate;
      if (isARM && month > armFixedTerm * 12) {
          currentRate = armSubsequentRate;
          // Recast
          if (month === (armFixedTerm * 12) + 1) {
              const remaining = totalMonths - (month - 1);
              // Recast P&I based on current balance for Finance Scenario
              if (mortgageBalance > 0) {
                 pAndI = calculateMonthlyPayment(mortgageBalance, currentRate, remaining);
              }
          }
      }
      const monthlyInterestRate = currentRate / 100 / 12;

      // --- Apply Lump Sums for this month ---
      while (lumpSumIndex < sortedLumpSums.length && Math.round(sortedLumpSums[lumpSumIndex].yearsFromNow * 12) === month) {
          const lump = sortedLumpSums[lumpSumIndex];
          mortgageBalance -= lump.amount;
          investedBalancePayCash += lump.amount;
          lumpSumIndex++;
      }

      // --- Finance Scenario Calculation ---
      const interestThisMonth = mortgageBalance > 0 ? mortgageBalance * monthlyInterestRate : 0;
      const deductibleInterestThisMonth = calculateDeductibleInterest(interestThisMonth, mortgageBalance, tax.filingStatus);
      deductibleInterestThisYear += deductibleInterestThisMonth;
      
      let principalThisMonth = 0;
      if (mortgageBalance > 0) {
          principalThisMonth = pAndI - interestThisMonth;
          if (principalThisMonth > mortgageBalance) principalThisMonth = mortgageBalance; // Final payment
          mortgageBalance -= principalThisMonth;
      }

      totalInterestPaid += interestThisMonth;
      totalPrincipalPaid += principalThisMonth;
      
      if(investedBalanceFinance > 0) {
        investedBalanceFinance *= (1 + monthlyReturnRate);
      }

      // --- Pay Cash Scenario Calculation ---
      investedBalancePayCash *= (1 + monthlyReturnRate);
      // Add the mortgage payment we DIDN'T make + insurance savings
      investedBalancePayCash += (pAndI + (insuranceSavings / 12));

      // --- Annual Tax Calculation (for Finance scenario) ---
      if (currentDate.getMonth() === 11 || month === totalMonths) {
        const yearsSinceStart = currentYear - startYear;
        const currentYearSaltCap = baseSaltCap * Math.pow(1.01, yearsSinceStart);
        const cappedOtherDeductions = Math.min(otherItemizedDeductions, currentYearSaltCap);
        
        const totalDeductions = deductibleInterestThisYear + cappedOtherDeductions;
        if (totalDeductions > standardDeduction) {
            const deductionWithoutMortgage = Math.max(standardDeduction, cappedOtherDeductions);
            const benefitFromMortgage = totalDeductions - deductionWithoutMortgage;
            const taxSavings = benefitFromMortgage * (tax.marginalTaxRate / 100);

            investedBalanceFinance += taxSavings; // Reinvest tax savings at year-end
            yearlyTaxSavings[currentYear] = (yearlyTaxSavings[currentYear] || 0) + taxSavings;
        }
        deductibleInterestThisYear = 0; // Reset for next year
      }

      // --- Record Monthly Data ---
      const homeEquity = homeValue - Math.max(0, mortgageBalance);
      monthlyData.push({
          month,
          date: currentDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short'}),
          year: currentYear,
          homeValue,
          // Finance
          mortgageBalance: Math.max(0, mortgageBalance),
          homeEquity,
          investedBalanceFinance,
          financeScenarioNetWorth: homeEquity + investedBalanceFinance,
          taxSavingsThisYear: yearlyTaxSavings[currentYear] || 0,
          // Pay Cash
          investedBalancePayCash,
          payCashScenarioNetWorth: homeValue + investedBalancePayCash,
      });
    }

    const lastMonthData = monthlyData[monthlyData.length - 1];

    return {
      summary: {
        loanAmount,
        downPaymentAmount,
        financeScenarioFinalNetWorth: lastMonthData.financeScenarioNetWorth,
        payCashScenarioFinalNetWorth: lastMonthData.payCashScenarioNetWorth,
        totalInterestPaid,
        totalPrincipalPaid,
        itemizationInfo: calculateItemizationInfo(home, tax),
      },
      monthlyData,
    };
};


export const useBuyNewHomeCalculator = (inputs: BuyNewHomeInputs | null): BuyNewHomeCalculationResults | null => {
  const [results, setResults] = useState<BuyNewHomeCalculationResults | null>(null);

  useEffect(() => {
    if (!inputs) {
      setResults(null);
      return;
    }

    const calculationResults = calculateBuyNewHome(inputs);
    setResults(calculationResults);

  }, [inputs]);

  return results;
};